import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useMemo, useState, useEffect, useCallback } from "react";

type QrType = "admin" | "academic" | "examination" | "accounting";

interface TimeWindow {
  start: string; // "HH:mm"
  end: string;
}

interface QrVisibilityRule {
  id: string;
  qr_type: string;
  visible_to_roles: string[];
  day_of_week: string[];
  time_windows: TimeWindow[];
  date_from: string | null;
  date_to: string | null;
  is_active: boolean;
  geo_enabled: boolean;
  geo_latitude: number | null;
  geo_longitude: number | null;
  geo_radius_meters: number | null;
  target_classes: string[];
}

interface VisibilityResult {
  isVisible: boolean;
  reason: string;
}

const DAY_NAMES = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];

function getEATDate(): Date {
  const now = new Date();
  const utc = now.getTime() + now.getTimezoneOffset() * 60000;
  return new Date(utc + 3 * 3600000);
}

function timeToMinutes(time: string): number {
  const [h, m] = time.split(":").map(Number);
  return h * 60 + (m || 0);
}

function checkTimeWindow(windows: TimeWindow[], currentMinutes: number): boolean {
  if (windows.length === 0) return true;
  return windows.some(w => {
    const start = timeToMinutes(w.start);
    const end = timeToMinutes(w.end);
    return currentMinutes >= start && currentMinutes <= end;
  });
}

function formatTime(time: string): string {
  const [h, m] = time.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = h % 12 || 12;
  return `${h12}:${String(m || 0).padStart(2, "0")} ${ampm}`;
}

/** Haversine distance in meters between two lat/lng points */
function haversineDistance(
  lat1: number, lng1: number,
  lat2: number, lng2: number
): number {
  const R = 6371000; // Earth radius in meters
  const toRad = (deg: number) => (deg * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLng = toRad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLng / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

export function useQrVisibility() {
  const { roles } = useAuth();
  const isStudent = roles.includes("student");

  // Track user's current geolocation for geofence checks
  const [userCoords, setUserCoords] = useState<{ lat: number; lng: number } | null>(null);

  useEffect(() => {
    if (!navigator.geolocation) return;
    const watchId = navigator.geolocation.watchPosition(
      (pos) => setUserCoords({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
      () => {}, // silently ignore errors
      { enableHighAccuracy: true, maximumAge: 30000 }
    );
    return () => navigator.geolocation.clearWatch(watchId);
  }, []);

  const { data: rules = [] } = useQuery({
    queryKey: ["qr-visibility-rules"],
    queryFn: async () => {
      const { data } = await supabase
        .from("qr_visibility_rules")
        .select("*")
        .eq("is_active", true);
      return (data || []) as unknown as QrVisibilityRule[];
    },
    staleTime: 60 * 1000,
  });

  // Master per-type override toggles (system_settings rows: qr_master_<type>)
  const { data: masterOverrides = {} } = useQuery({
    queryKey: ["qr-master-overrides"],
    queryFn: async () => {
      const { data } = await supabase
        .from("system_settings")
        .select("key,value")
        .like("key", "qr_master_%");
      const map: Record<string, boolean> = {};
      (data || []).forEach((row: any) => {
        const type = row.key.replace("qr_master_", "");
        // value stored as { enabled: boolean }
        const v = row.value;
        map[type] = typeof v === "object" && v !== null ? !!v.enabled : !!v;
      });
      return map;
    },
    staleTime: 30 * 1000,
  });

  const checkVisibility = useCallback(
    (qrType: QrType, isOwnQr: boolean = false, studentClass?: string | null): VisibilityResult => {
      // Student viewing their own QR codes: always visible so staff can scan them.
      // Visibility rules (time/geo/role/class) only gate staff-side scanning, not the
      // student's display of their personal identity codes on their portal.
      if (isOwnQr) {
        if (masterOverrides[qrType] === false) {
          return { isVisible: false, reason: "QR code temporarily disabled by administrator" };
        }
        return { isVisible: true, reason: "Active" };
      }

      // Master override: if explicitly disabled, hide regardless of rules
      if (masterOverrides[qrType] === false) {
        return { isVisible: false, reason: "QR code temporarily disabled by administrator" };
      }
      // Master override: if explicitly enabled, bypass all rule checks
      if (masterOverrides[qrType] === true) {
        return { isVisible: true, reason: "Active (master override)" };
      }

      const eat = getEATDate();
      const currentDay = DAY_NAMES[eat.getDay()];
      const currentMinutes = eat.getHours() * 60 + eat.getMinutes();
      const currentDateStr = eat.toISOString().split("T")[0];

      const typeRules = rules.filter(r => r.qr_type === qrType);

      // No rules configured → hidden for students, default role-based for staff
      if (typeRules.length === 0) {
        if (isOwnQr) return { isVisible: false, reason: "Not configured — contact administration" };
        return { isVisible: false, reason: "No visibility rules configured" };
      }

      for (const rule of typeRules) {
        // Role check (for staff viewing student QR codes)
        if (!isOwnQr) {
          const roleMatch = rule.visible_to_roles.length === 0 || roles.some(r => rule.visible_to_roles.includes(r));
          if (!roleMatch) continue;
        }

        // Target class check
        const targetClasses = Array.isArray(rule.target_classes) ? rule.target_classes : [];
        if (targetClasses.length > 0 && studentClass) {
          if (!targetClasses.includes(studentClass)) continue;
        }

        // Date range check
        if (rule.date_from && currentDateStr < rule.date_from) continue;
        if (rule.date_to && currentDateStr > rule.date_to) continue;

        // Day of week check
        if (rule.day_of_week.length > 0 && !rule.day_of_week.includes(currentDay)) {
          const windows = rule.time_windows || [];
          const nextWindow = windows.length > 0 ? formatTime(windows[0].start) : "";
          return {
            isVisible: false,
            reason: `Available on ${rule.day_of_week.map(d => d.charAt(0).toUpperCase() + d.slice(1)).join(", ")}${nextWindow ? ` at ${nextWindow}` : ""}`,
          };
        }

        // Time window check
        const windows = Array.isArray(rule.time_windows) ? rule.time_windows as TimeWindow[] : [];
        if (windows.length > 0 && !checkTimeWindow(windows, currentMinutes)) {
          const upcoming = windows.find(w => timeToMinutes(w.start) > currentMinutes);
          if (upcoming) {
            return { isVisible: false, reason: `Available today at ${formatTime(upcoming.start)}` };
          }
          return { isVisible: false, reason: `Available ${formatTime(windows[0].start)} - ${formatTime(windows[0].end)}` };
        }

        // Geofence check
        if (rule.geo_enabled && rule.geo_latitude != null && rule.geo_longitude != null) {
          if (!userCoords) {
            return { isVisible: false, reason: "Location not available — please enable location services" };
          }
          const distance = haversineDistance(
            userCoords.lat, userCoords.lng,
            rule.geo_latitude, rule.geo_longitude
          );
          const radius = rule.geo_radius_meters || 100;
          if (distance > radius) {
            return { isVisible: false, reason: "You are outside the allowed area for this QR code" };
          }
        }

        // All checks passed
        return { isVisible: true, reason: "Active" };
      }

      // No matching rule found
      if (isOwnQr) {
        return { isVisible: false, reason: "QR code is currently not available" };
      }
      return { isVisible: false, reason: "No matching visibility rule" };
    },
    [rules, roles, userCoords, masterOverrides]
  );

  return { checkVisibility, rules, isStudent };
}
